import React, { useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import {
  DeviceAlertStatus,
  ISubscriptionDeviceRow,
  deriveDeviceStatus,
} from '@data/subscriptions/model/subscriptions.model'
import { useBillingEntities } from '@data/billing/billing'
import SharePaymentLinkModal from './SharePaymentLinkModal'

export type InvoiceQuickFilter = 'all' | 'paid' | 'open' | 'past_due' | 'draft'

interface Props {
  devices: ISubscriptionDeviceRow[] | undefined
  // Single-account context enables the "Share with Accounts" button.
  // When omitted (e.g. Home dashboard with multiple accounts selected), share is hidden.
  accountId?: number
  onViewInvoices?: (filter: InvoiceQuickFilter) => void
}

// Only states that call for action get a banner. 'paid', 'healthy',
// 'pending' and 'no_subscription' need none, so this is listed explicitly
// rather than derived from DeviceAlertStatus.
type BannerKey = 'restricted' | 'overdue' | 'upcoming'

interface BannerSpec {
  key: BannerKey
  className: string
  title: (n: number) => string
  body: string
  invoiceFilter: InvoiceQuickFilter
}

const BANNERS: BannerSpec[] = [
  {
    key: 'restricted',
    className: 'sf-alert sf-alert--restricted',
    title: (n) => `Access restricted: ${n} device${n === 1 ? '' : 's'}`,
    body: 'Your account access is restricted due to an outstanding balance. Healthy devices remain operational.',
    invoiceFilter: 'past_due',
  },
  {
    key: 'overdue',
    className: 'sf-alert sf-alert--restricted',
    title: (n) => `Payment overdue: ${n} device${n === 1 ? '' : 's'}`,
    body: 'Payment is overdue. Please settle the outstanding balance to avoid access restrictions.',
    invoiceFilter: 'past_due',
  },
  {
    key: 'upcoming',
    className: 'sf-alert sf-alert--upcoming',
    title: (n) => `Upcoming renewal: ${n} device${n === 1 ? '' : 's'} due in 30 days`,
    body: 'Stripe has issued renewal invoices. Please review and pay before the renewal date to avoid disruption.',
    invoiceFilter: 'open',
  },
]

const SubscriptionAlerts: React.FC<Props> = ({ devices, accountId, onViewInvoices }) => {
  const [shareEntityId, setShareEntityId] = useState<number | null>(null)
  const { data: billingEntities } = useBillingEntities(accountId)

  const counts = useMemo(() => {
    const acc: Record<BannerKey, number> = {
      restricted: 0,
      overdue: 0,
      upcoming: 0,
    }
    for (const d of devices ?? []) {
      const s = deriveDeviceStatus(d)
      if (s in acc) acc[s as BannerKey] += 1
    }
    return acc
  }, [devices])

  // Render only the highest-priority banner (banners are listed in precedence order).
  const banner = BANNERS.find((b) => counts[b.key] > 0)
  if (!banner) return null

  const handleShare = () => {
    const first = billingEntities?.[0]
    if (first) setShareEntityId(first.id)
  }

  return (
    <div className="mb-3">
      <div className="text-muted small fw-semibold mb-2">ALERTS</div>
      <div className={`${banner.className} d-flex justify-content-between align-items-center`}>
        <div className="me-3">
          <div className="fw-bold">{banner.title(counts[banner.key])}</div>
          <div className="small opacity-90">{banner.body}</div>
        </div>
        <div className="d-flex flex-column gap-2">
          <Button
            size="sm"
            variant="light"
            onClick={() => onViewInvoices?.(banner.invoiceFilter)}
            disabled={!onViewInvoices}
          >
            View Invoices
          </Button>
          {accountId !== undefined && (
            <Button
              size="sm"
              variant="outline-light"
              onClick={handleShare}
              disabled={!billingEntities || billingEntities.length === 0}
            >
              Share with Accounts
            </Button>
          )}
        </div>
      </div>
      {shareEntityId !== null && (
        <SharePaymentLinkModal
          billingEntityId={shareEntityId}
          show={shareEntityId !== null}
          onHide={() => setShareEntityId(null)}
        />
      )}
    </div>
  )
}

export default SubscriptionAlerts
